import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, Paper, Skeleton, Typography } from '@mui/material';

/** Dashed content region: loading spinner or empty-state text — same on all admin panels. */
export const adminPanelPlaceholderSx = {
  minHeight: 220,
  borderRadius: 2,
  border: '1px dashed',
  borderColor: 'divider',
  bgcolor: 'action.hover',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1.5,
  px: 2
};

export function AdminPanelLoading({ label = 'Loading…', sx, ...props }) {
  return (
    <Box
      sx={{ ...adminPanelPlaceholderSx, ...sx }}
      aria-busy="true"
      aria-label={label}
      {...props}
    >
      <CircularProgress size={28} sx={{ color: 'primary.light' }} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cached count + skeleton — remembers how many rows a panel had last time so
// the next load draws the right amount of placeholder cards instead of a
// generic spinner.
// ─────────────────────────────────────────────────────────────────────────────

const COUNT_KEY_PREFIX = 'panel-count:';
const MIN_COUNT = 1;
const MAX_COUNT = 24;

const clamp = (n) => Math.max(MIN_COUNT, Math.min(MAX_COUNT, n));

function readCount(key, fallback) {
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(`${COUNT_KEY_PREFIX}${key}`);
    if (raw == null) return fallback;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) return fallback;
    return clamp(n);
  } catch { return fallback; }
}

function writeCount(key, value) {
  if (!key) return;
  try {
    const n = Math.max(0, Math.min(MAX_COUNT, value | 0));
    localStorage.setItem(`${COUNT_KEY_PREFIX}${key}`, String(n));
  } catch { /* quota exceeded — silently ignore */ }
}

/**
 * Reads/writes a panel's last-known item count from localStorage. Returns
 * `[count, setCount]` where `count` is always within [MIN_COUNT, MAX_COUNT]
 * for safe rendering of skeleton rows. Pass a falsy `key` to disable.
 */
export function useCachedPanelCount(key, fallback = 6) {
  const safeFallback = clamp(fallback);
  const [count, setCount] = useState(() => readCount(key, safeFallback));

  // Re-read whenever the key changes (e.g. user switches teams).
  useEffect(() => {
    setCount(readCount(key, safeFallback));
  }, [key, safeFallback]);

  const update = useCallback((value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return;
    writeCount(key, value);
    setCount(value > 0 ? clamp(value) : safeFallback);
  }, [key, safeFallback]);

  return [count, update];
}

const SKELETON_BG = 'rgba(255,255,255,0.05)';
const SKELETON_BG_DIM = 'rgba(255,255,255,0.035)';

function GridSkeletonCard() {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', bgcolor: 'transparent' }}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Skeleton
          variant="rounded"
          width={48}
          height={48}
          animation="wave"
          sx={{ bgcolor: SKELETON_BG, flexShrink: 0, borderRadius: '8px' }}
        />
        <Box flex={1} minWidth={0}>
          <Skeleton variant="text" width="65%" height={18} animation="wave" sx={{ bgcolor: SKELETON_BG }} />
          <Skeleton variant="text" width="40%" height={14} animation="wave" sx={{ bgcolor: SKELETON_BG_DIM }} />
        </Box>
      </Box>
    </Paper>
  );
}

function ListSkeletonRow() {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', bgcolor: 'transparent' }}>
      <Box display="flex" alignItems="center" gap={1} mb={1.25}>
        <Skeleton variant="text" width={160} height={18} animation="wave" sx={{ bgcolor: SKELETON_BG }} />
      </Box>
      <Box display="flex" gap={0.75} flexWrap="wrap">
        <Skeleton variant="rounded" width={64} height={18} animation="wave" sx={{ bgcolor: SKELETON_BG_DIM, borderRadius: '9999px' }} />
        <Skeleton variant="rounded" width={88} height={18} animation="wave" sx={{ bgcolor: SKELETON_BG_DIM, borderRadius: '9999px' }} />
        <Skeleton variant="rounded" width={52} height={18} animation="wave" sx={{ bgcolor: SKELETON_BG_DIM, borderRadius: '9999px' }} />
      </Box>
    </Paper>
  );
}

function LogSkeletonRow() {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: '8px',
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Skeleton variant="rounded" width={42} height={18} animation="wave" sx={{ bgcolor: SKELETON_BG }} />
        <Skeleton variant="rounded" width={56} height={18} animation="wave" sx={{ bgcolor: SKELETON_BG_DIM }} />
        <Skeleton variant="text" width={90} height={14} animation="wave" sx={{ bgcolor: SKELETON_BG_DIM, ml: 'auto' }} />
      </Box>
      <Skeleton variant="text" width="72%" height={12} animation="wave" sx={{ bgcolor: SKELETON_BG_DIM, mt: 0.5 }} />
    </Box>
  );
}

/**
 * Shimmer skeleton for admin panels. Pick a `variant` that matches the panel's
 * actual layout and a `count` that matches the expected row count (typically
 * sourced from `useCachedPanelCount`).
 *
 * - `grid` — 3-col card grid (Characters, Chapters, Saves, Insta, Storage)
 * - `list` — stacked rows with chips (Teams, Embeddings)
 * - `log`  — compact one-line rows (AI Logs)
 */
export function AdminPanelSkeleton({ variant = 'grid', count = 6, sx }) {
  const safeCount = clamp(count || MIN_COUNT);

  if (variant === 'grid') {
    return (
      <Grid container spacing={1.5} sx={sx} aria-busy="true" aria-label="Loading…">
        {Array.from({ length: safeCount }).map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <GridSkeletonCard />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (variant === 'log') {
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...sx }}
        aria-busy="true"
        aria-label="Loading…"
      >
        {Array.from({ length: safeCount }).map((_, i) => <LogSkeletonRow key={i} />)}
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1.5}
      sx={sx}
      aria-busy="true"
      aria-label="Loading…"
    >
      {Array.from({ length: safeCount }).map((_, i) => <ListSkeletonRow key={i} />)}
    </Box>
  );
}
